import type { FC } from 'react';
import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { SuggestedReply } from '@/app/lib/a2ui/blocks/SuggestedReply.tsx';
import { useSendIntent } from '@/app/lib/a2ui/blocks/use-send-intent.ts';
import { arr, str } from '@/app/lib/a2ui/props.ts';
import { isRecord } from '@/app/lib/util/type-guards.ts';
import { CalendarCheck, Clock3, MapPin, ShieldCheck } from 'lucide-react';

const TRUST_FACTS = [
  { label: 'Free site visits in Delhi NCR', Icon: CalendarCheck },
  { label: '5-year plywood warranty', Icon: ShieldCheck },
  { label: '3–5 weeks after design approval', Icon: Clock3 },
  { label: 'Delhi NCR service area', Icon: MapPin },
] as const;

const CONCEPTS = [
  {
    label: 'Kitchen',
    src: 'https://storage.agentplace.io/0_-kAIH73Cd0urxQbZv36nvVMRcfg4uO8gPDy054muQ/uktkq1tocpsx/media/generated-72f22f3a2b90eda8ed7bd6dbd481575e0d074b64b52dc386d9e5f48734b171f2-lakdi-concept-kitchen.png',
    alt: 'Illustrative concept of a cream and walnut contemporary modular kitchen.',
  },
  {
    label: 'Wardrobe',
    src: 'https://storage.agentplace.io/0_-kAIH73Cd0urxQbZv36nvVMRcfg4uO8gPDy054muQ/uktkq1tocpsx/media/generated-eefe445466fc3b2ab50fe8498c1c19e72d3b4b1d4704ff629f6e2c3f562ec19a-lakdi-concept-wardrobe.png',
    alt: 'Illustrative concept of a floor-to-ceiling walnut and ivory wardrobe.',
  },
  {
    label: 'TV unit',
    src: 'https://storage.agentplace.io/0_-kAIH73Cd0urxQbZv36nvVMRcfg4uO8gPDy054muQ/uktkq1tocpsx/media/generated-f0dda9dc4b7e0a7de4e242462f349f05881debe69a80d7f502dea8a749889155-lakdi-concept-tv-unit.png',
    alt: 'Illustrative concept of a custom walnut and cream TV unit in a living room.',
  },
  {
    label: 'Bedroom',
    src: 'https://storage.agentplace.io/0_-kAIH73Cd0urxQbZv36nvVMRcfg4uO8gPDy054muQ/uktkq1tocpsx/media/generated-e1c695ac990e21dacbce255d7a37dc2cf911804bcbf2bea78afbbe12048d81a3-lakdi-concept-bedroom.png',
    alt: 'Illustrative concept of a calm bedroom with a custom upholstered storage bed.',
  },
] as const;

const CHOICE_LABELS: Record<string, string> = {
  kitchen: 'Kitchen',
  wardrobe: 'Wardrobe',
  tv_unit: 'TV unit',
  bed: 'Bed',
  full_room: 'Full room',
  just_question: 'Just a question',
};

export const LakdiWelcome: FC<A2uiNodeViewProps> = ({ node }) => {
  const sendIntent = useSendIntent();
  const intro = str(node.props.intro);
  const serviceSummary = str(node.props.serviceSummary);
  const question = str(node.props.question);
  const estimateIntent = str(node.props.estimateIntent);
  const continueIntent = str(node.props.continueIntent);
  const choices = arr(node.props.choices).filter(isRecord);

  return (
    <main className="@container/lakdi mx-auto w-full max-w-container-2xl min-w-0">
      <article
        aria-label="Lakdi Studio — Custom Furniture & Interiors"
        className="w-full overflow-hidden rounded-3xl border border-border bg-card shadow-elevated"
      >
        <section className="bg-gradient-to-br from-card via-muted to-primary/10 px-5 py-7 @md/lakdi:px-9 @md/lakdi:py-10">
          <div className="max-w-3xl space-y-4">
            <p className="text-sm font-semibold tracking-wide text-primary" translate="no">
              Lakdi Studio
            </p>
            <h1 className="break-words font-display text-balance text-4xl font-medium leading-[0.98] tracking-tight text-foreground @md/lakdi:text-5xl">
              Custom Furniture &amp; Interiors
            </h1>
            <div className="max-w-prose space-y-3">
              {intro ? <p className="text-base leading-relaxed text-foreground">{intro}</p> : null}
              {serviceSummary ? (
                <p className="text-base leading-relaxed text-muted-foreground">{serviceSummary}</p>
              ) : null}
            </div>
          </div>

          <ul className="mt-7 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-primary/15 pt-5 @xl/lakdi:grid-cols-4">
            {TRUST_FACTS.map(({ label, Icon }) => (
              <li key={label} className="flex min-w-0 items-center gap-2 text-base leading-snug text-foreground">
                <Icon className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
                <span className="min-w-0 break-words">{label}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="border-t border-border px-5 py-6 @md/lakdi:px-9">
          <div className="mb-4 space-y-1">
            <h2 className="font-display text-2xl font-semibold tracking-tight text-foreground">
              Design inspiration
            </h2>
            <p className="text-base leading-relaxed text-muted-foreground">
              Illustrative design concepts—not completed Lakdi Studio projects.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 @xl/lakdi:grid-cols-4 @xl/lakdi:gap-4">
            {CONCEPTS.map((concept) => (
              <figure
                key={concept.label}
                className="group min-w-0 overflow-hidden rounded-2xl border border-border bg-card shadow-sm"
              >
                <div className="aspect-[4/3] overflow-hidden bg-muted">
                  <img
                    src={concept.src}
                    alt={concept.alt}
                    width={1024}
                    height={1024}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-150 ease-out group-hover:scale-[1.02] motion-reduce:transition-none"
                  />
                </div>
                <figcaption className="px-3 py-2.5 text-base font-medium text-foreground">
                  {concept.label}
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="border-t border-border bg-background/60 px-5 py-6 @md/lakdi:px-9">
          {question ? (
            <h2 className="mb-4 text-balance text-lg font-semibold leading-snug text-foreground">
              {question}
            </h2>
          ) : null}
          <div className="flex flex-wrap gap-2">
            {choices.map((choice) => {
              const id = str(choice.id);
              const label = CHOICE_LABELS[id];
              const intent = str(choice.intent);
              if (!label) return null;
              return (
                <SuggestedReply
                  key={id}
                  disabled={!intent}
                  className="min-h-11 rounded-full border border-border bg-card px-4 shadow-sm transition-colors duration-150 hover:border-primary hover:bg-muted hover:no-underline focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-card motion-reduce:transition-none"
                  onClick={() => sendIntent(intent)}
                >
                  {label}
                </SuggestedReply>
              );
            })}
          </div>
          <div className="mt-4 flex flex-col gap-2">
            <SuggestedReply
              disabled={!estimateIntent}
              className="min-h-12 w-full justify-between rounded-xl border border-primary bg-primary px-5 py-3 font-semibold text-primary-foreground shadow hover:bg-primary/90 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-card"
              onClick={() => sendIntent(estimateIntent)}
            >
              Get a free estimate
            </SuggestedReply>
            {continueIntent ? (
              <SuggestedReply
                className="min-h-11 w-full rounded-xl border border-border bg-card px-5 shadow-sm hover:bg-muted hover:no-underline"
                onClick={() => sendIntent(continueIntent)}
              >
                Continue previous plan
              </SuggestedReply>
            ) : null}
          </div>
        </section>
      </article>
    </main>
  );
};
